<template>
	<div class="text-body-l text-neutral-950">
		<NuxtLayout>
			<NuxtPage />
		</NuxtLayout>
		<ClientOnly>
			<EnvironmentBadge :environment="Settings.retrieveEnv()" :is-x-s="deviceStore.isXS" />
			<RootDialog />
			<NotificationsHandler />
			<QueuedDialogs :profile="profile" />
		</ClientOnly>
	</div>
</template>

<script setup lang="ts">
	import { onMounted } from 'vue'
	import { useCookie } from 'nuxt/app'
	import { MixpanelService } from '~/services/mixpanel'
	import { TranslationService } from '~/services/Translation'
	import { useDeviceStore } from '~/store/device'
	import { BaseMetric } from './actions/metrics/baseMetric'
	import NotificationsHandler from './components/NotificationsHandler.vue'
	import CookieService, { AuthCookie } from '~/services/cookies'
	import { Profile } from './models/Profile'
	import { storeToRefs } from 'pinia'
	import { useProfileStore } from './store/profile'
	import { ProfileService } from './passporter-services/profile/service'
	import { AuthNavigation } from './services/navigation/authNavigation'
	import Settings from '~/services/settings'

	const i18n = useI18n()

	if (!Settings.isProd()) {
		useHead({
			titleTemplate: (title) => `[${Settings.retrieveEnv().toUpperCase()} W3] ${title}`,
		})
	}

	const deviceStore = useDeviceStore()
	const profileStore = useProfileStore()
	const { profile } = storeToRefs(profileStore)
	const { gtag } = useGtag()
	BaseMetric.setGtag(gtag)
	MixpanelService.init()
	TranslationService.getInstance(i18n).setLocale(i18n.locale.value)

	const authCookie = CookieService.getAuthCookie()

	if (authCookie?.auth?.accessToken) await getProfile()

	onMounted(() => {
		update()
		window.addEventListener('resize', update)
	})

	function update() {
		const size = window.innerWidth
		deviceStore.updateSize(size)
	}

	async function getProfile() {
		try {
			const cookie = useCookie<AuthCookie>('authentication-cookie')
			const userId = cookie?.value?.auth?.id
			if (!userId) return resetSession()
			if (import.meta.client) MixpanelService.identify(userId)
			const newProfile: Profile = await ProfileService.getProfile(userId)
			if (!newProfile?.id) return resetSession()
			profileStore.setProfile(newProfile)
			return profile
		} catch (e) {
			console.error(e)
			return {}
		}
	}

	async function resetSession() {
		useCookie('authentication-cookie').value = null
		if (import.meta.client) MixpanelService.reset()
		return AuthNavigation.toLogin()
	}
</script>
